import classNames from "classnames";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import {
	CSSProperties,
	KeyboardEvent,
	ReactElement,
	Ref,
	forwardRef,
	useMemo
} from "react";
import {
	FontIconName,
	Icon,
	isSvgIcon,
	SvgIconValue
} from "../Icon/Icon";
import styles from "./ActionListItem.module.less";
import { useIndention } from "./ActionListItem";

export type ActionListHeaderProps = {
	text: string;
	suffix?: string;
	isDisabled?: boolean;
	trackingId?: string;
	rightIcon?: FontIconName | SvgIconValue;
	onClick?: () => void;
	onKeyUp?: (args: KeyboardEvent<HTMLLIElement>) => void;
	onKeyDown?: (args: KeyboardEvent<HTMLLIElement>) => void;
	tabIndex?: number;
	indentionLevel?: number;
	hasLine?: boolean;
	role?: "option" | "group";
};

export const ActionListHeader = forwardRef(
	(props: ActionListHeaderProps, ref: Ref<HTMLLIElement>): ReactElement => {
		const {
			text,
			suffix,
			isDisabled = false,
			trackingId,
			rightIcon,
			onClick: onClickFromProps,
			onKeyUp,
			onKeyDown,
			tabIndex,
			indentionLevel,
			hasLine = false,
			role
		} = props;

		const onClick = useMemo(
			(): (() => void) | undefined => {
				if (isDisabled || !onClickFromProps) {
					return undefined;
				}

				return (): void => onClickFromProps();
			},
			[isDisabled, onClickFromProps]
		);

		const style: CSSProperties = useIndention(indentionLevel);

		return (
			<li
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_disabled]: isDisabled,
						[styles.root_hoverable]: !isDisabled && isNotNil(onClickFromProps)
					}
				)}
				style={style}
				onClick={onClick}
				onKeyUp={onKeyUp}
				onKeyDown={onKeyDown}
				tabIndex={tabIndex}
				role={role}
				ref={ref}
			>
				<div
					className={classNames(
						styles.contentHost,
						{[styles.contentHost_hasLine]: hasLine}
					)}
				>
					<span
						className={classNames(
							styles.text,
							styles.text_header
						)}
					>
						{text}
					</span>

					<div className={styles.rightAndSubMenuIcon}>
						{suffix && (
							<span
								className={classNames(
									styles.suffix_header
								)}
							>
								{suffix}
							</span>
						)}

						{rightIcon && (
							<span
								className={classNames(
									styles.icon,
									styles.icon_right,
									{ [styles.icon_svg]: isSvgIcon(rightIcon) }
								)}
							>
								<Icon value={rightIcon} />
							</span>
						)}
					</div>

					{hasLine && (
						<hr className={styles.line} />
					)}
				</div>
			</li>
		);
	}
);
ActionListHeader.displayName = "ActionListHeader";
